<template>
	<CardContext 
        ref="card"
        :app="app"
        :card="card" 
        :actionable="true"
        :actions="actions" 
        :guidance="guidance"
        :error="error"
        :message="message" 
        @init="handleInit"
        @update:loading="handleLoading" 
        @action="handleAction">

		<template #title>
			{{ title }}
		</template>

		<template #context>

            <!-- Mode -->
            <v-btn-toggle 
                v-if="false"
                mandatory 
                class="toggle" 
                elevation="2" 
                v-model="currentMode">

                <!-- View -->
                <v-btn 
                    class="toggle-button" 
                    elevation="2" 
                    :class="{ 'active': isModeView }"
                    :value="keyModeView">
                    <v-tooltip 
                        activator="parent" 
                        :location="tooltipLocation">
                        view model
                    </v-tooltip>
                    <v-icon icon="fa-solid fa-camera"></v-icon>
                </v-btn>

                <!-- Modify -->
                <v-btn 
                    class="toggle-button" 
                    elevation="2" 
                    :class="{ 'active': isModeModify }"
                    :value="keyModeModify">
                    <v-tooltip 
                        activator="parent" 
                        :location="tooltipLocation">
                        highlight changes
                    </v-tooltip>
                    <v-icon icon="fa-solid fa-highlighter"></v-icon>
                </v-btn>

            </v-btn-toggle>

            <Separator />

			<!-- Play / Pause -->
            <v-btn 
                color="darkbase"
            	elevation="2" 
                class="button" 
                :class="{ 'active': animated }"
                :disabled="isModeModify"
                @click="handleToggleAnimation">
                <v-tooltip
                    activator="parent"
                    :location="tooltipLocation">
                    <span v-if="animated">pause animation</span>
                    <span v-else>play animation</span>
                </v-tooltip>
                <v-icon 
                    v-if="animated" 
                    icon="fa-solid fa-pause">
                </v-icon>
                <v-icon 
                    v-else 
                    icon="fa-solid fa-play">
                </v-icon>
            </v-btn>

            <Separator />

            <!-- Rating -->
            <v-btn
                color="darkbase"
            	elevation="2" 
                :disabled="isModeModify"
                @click="handleRating">
                <v-tooltip
                    activator="parent"
                    :location="tooltipLocation">
                    rate model
                </v-tooltip>
                <v-icon 
                    v-if="hasRating" 
                    icon="fa-solid fa-star">
                </v-icon>
                <v-icon 
                    v-else 
                    icon="fa-regular fa-star">
                </v-icon>
            </v-btn>

            <!-- Share -->
            <v-btn
                color="darkbase"
            	elevation="2" 
                :disabled="isModeModify"
                @click="handleShare">
                <v-tooltip
                    activator="parent"
                    :location="tooltipLocation">
                    share model
                </v-tooltip>                
                <v-icon icon="fa-solid fa-share-nodes">
                </v-icon>
            </v-btn>

            <!-- Download -->
            <v-btn
                color="darkbase"
            	elevation="2" 
                :disabled="isModeModify"
                @click="handleDownload">
                <v-tooltip
                    activator="parent"
                    :location="tooltipLocation">
                    download model
                </v-tooltip>
                <v-icon icon="fa-solid fa-download"></v-icon>
            </v-btn>

            <!-- Showcase -->
            <v-btn
                v-if="isShowcaseSupported"
                color="darkbase"
            	elevation="2" 
                :disabled="isModeModify"
                @click="handleShowcase">
                <v-tooltip
                    activator="parent"
                    :location="tooltipLocation">
                    showcase in augmented reality
                </v-tooltip>
                <v-icon icon="fa-solid fa-vr-cardboard"></v-icon>
            </v-btn>

		</template>

        <template #layer>

            <v-btn-toggle 
                mandatory 
                class="toggle" 
                elevation="2" 
                v-model="currentLayer">

                <!-- Transparent -->
                <v-btn 
                    v-if="hasLayerTransparent" 
                    class="toggle-button" 
                    elevation="2" 
                    :class="{ 'active': isLayerTransparent }"
                    :value="keyViewerLayerTransparent">
                    <v-tooltip 
                        activator="parent" 
                        location="start" 
                        class="hide-on-mobile">
                        transparent rendering
                    </v-tooltip>
                    <v-icon icon="fa-solid fa-circle-half-stroke"></v-icon>
                </v-btn>

                <!-- Texture -->
                <v-btn 
                    v-if="hasLayerTexture" 
                    class="toggle-button" 
                    elevation="2" 
                    :class="{ 'active': isLayerTexture }"
                    :value="keyViewerLayerTexture">
                    <v-tooltip 
                        activator="parent" 
                        location="start" 
                        class="hide-on-mobile">
                        texture rendering
                    </v-tooltip>
                    <v-icon icon="fa-solid fa-chess-board"></v-icon>
                </v-btn>

                <!-- Solid -->
                <v-btn 
                    v-if="hasLayerSolid" 
                    class="toggle-button" 
                    elevation="2" 
                    :class="{ 'active': isLayerSolid }"
                    :value="keyViewerLayerSolid">
                    <v-tooltip 
                        activator="parent" 
                        location="start"
                        class="hide-on-mobile">
                        solid rendering
                    </v-tooltip>
                    <v-icon icon="fa-solid fa-square"></v-icon>
                </v-btn>

                <!-- Wireframe -->
                <v-btn 
                    v-if="hasLayerWireframe" 
                    class="toggle-button" 
                    elevation="2" 
                    :class="{ 'active': isLayerWireframe }" 
                    :value="keyViewerLayerWireframe">
                    <v-tooltip 
                        activator="parent" 
                        location="start" 
                        class="hide-on-mobile">
                        wireframe rendering
                    </v-tooltip>
                    <v-icon icon="fa-solid fa-draw-polygon"></v-icon>
                </v-btn>

                <!-- Structure -->
                <v-btn 
                    v-if="hasLayerStructure" 
                    class="toggle-button" 
                    elevation="2" 
                    :class="{ 'active': isLayerStructure }" 
                    :value="keyViewerLayerStructure">
                    <v-tooltip 
                        activator="parent" 
                        location="start" 
                        class="hide-on-mobile">
                        voxel rendering
                    </v-tooltip>
                    <v-icon icon="fa-solid fa-cubes"></v-icon>
                </v-btn>

            </v-btn-toggle>

        </template>

		<template #main>
            <div class="card-model-main">
                <ViewerModelRaw 
                    ref="viewer"
                    :file="currentFile" 
                    :mode="currentMode" 
                    :stage="currentStage"
                    :layer="currentLayer" 
                    :generating="generating"
                    :animated="animated" 
                    :environment="true" 
                    :floor="false"
                    :targetDistance="2.2" 
                    :flip="true"
                    @pause="handlePause" 
                    @loaded="handleLoaded" 
                    @selection="handleSelection" />

                <Details :asset="currentAsset" />
            </div>
		</template>

	</CardContext>
</template>

<script>
import ASSETS from '@generio-ai/library-legacy/constants/assets'
import CATEGORY from '@generio-ai/library-legacy/constants/category'
import VIEWER from '@generio-ai/library-legacy/constants/viewer'
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import Details from '@generio-ai/library-legacy/components/details'
import { ViewerModelRaw } from '@generio-ai/library-legacy/components/viewer'
import ACTION from '@/constants/action.js'
import CARD from '@/constants/card.js'
import { isAndroidDevice } from '@/utils/device.js'
import Separator from '@/components/common/toolbar/Separator.vue'
import CardContext from '@/components/flow/cards/CardContext.vue'

export default {
	name: 'CardModel',
	components: {
        ViewerModelRaw,
        Details,
        Separator,
        CardContext
	},
    props: {
        app: {
            type: String,
            required: true
        },
		card: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
            healthy: {
                texture: true,
			},
            error: false,
            loading: false,
            message: '',
            animated: null,
            timer: null,
            observedAsset: null,
            actionsKey: 0,
            generating: false,
            loadedCoords: false,
            loadedDataGeometry: false,
            loadedDataTexture: false,
            texture: false,
            appearance: false,
            transparent: false,
            currentMode: VIEWER.MODE.VIEW,
            currentStage: null,
            currentLayer: null,
            currentAsset: null,
            currentFile: null,
            selected: 0
		}
	},
	computed: {
        ...mapGetters('auth', [
            'isAdministrator'
        ]),
        ...mapGetters('assets', [
            'getFirstAsset',
            'getFileFromAsset'
        ]),
        ...mapGetters('deployment', [
            'getUrl'
        ]),
        ...mapGetters('navigation', [
            'isLocked',
        ]),
        ...mapGetters('flows', [
            'isActiveByIndex',
            'isLastByIndex',
            'isClonableByCard',
            'isFullscreen'
        ]),


        keyFileStateRetrieved() {
            return ASSETS.FILES.STATE.RETRIEVED
        },

        keyModeView() {
            return VIEWER.MODE.VIEW
        },
        keyModeModify() {
            return VIEWER.MODE.MODIFY
        },

        keyViewerLayerTransparent() {
            return VIEWER.LAYER.TRANSPARENT
        },
        keyViewerLayerTexture() {
            return VIEWER.LAYER.TEXTURE
        },
        keyViewerLayerSolid() {
            return VIEWER.LAYER.SOLID
        },
        keyViewerLayerWireframe() {
            return VIEWER.LAYER.WIREFRAME
        },
        keyViewerLayerStructure() {
            return VIEWER.LAYER.STRUCTURE
        },

        hasLayerTransparent() {
            return this.transparent
        },
        hasLayerTexture() {
            return this.appearance || this.texture
        },
        hasLayerSolid() {
            return true
        },
        hasLayerWireframe() {
            return true
        },
        hasLayerStructure() {
            return this.loadedCoords
        },

        hasSelection() {
            return this.selected > 0
        },

        hasParent() {
            return this.parent !== null && this.parent !== undefined
        },
		hasPromptUser() {
			return this.promptUser !== null && this.promptUser !== undefined && this.promptUser !== ''
		},
        hasRating() {
            return this.rating > 0
        },

        isTextureHealthy() {
            return this.healthy.texture
        },

        isLegacy() {
            return this.version < 10
        },
        
        isWatertight() {
            return this.fileDetails?.watertight ?? false
        },

		isActiveCard() {
			return this.isActiveByIndex(this.app, this.index)
		},
		isLastCard() {
            return this.isLastByIndex(this.app, this.index)
        },
        isClonable() {
            return this.isClonableByCard(this.card)
        },

        isShowcaseSupported() {
            return isAndroidDevice() //|| isIOSDevice()
        },

        isModeView() {
            return this.currentMode === VIEWER.MODE.VIEW
        },
        isModeModify() {
            return this.currentMode === VIEWER.MODE.MODIFY
        },

        isLayerTransparent() {
            return this.currentLayer === VIEWER.LAYER.TRANSPARENT
        },
        isLayerTexture() {
            return this.currentLayer === VIEWER.LAYER.TEXTURE
        },
        isLayerSolid() {
            return this.currentLayer === VIEWER.LAYER.SOLID
        },
        isLayerWireframe() {
            return this.currentLayer === VIEWER.LAYER.WIREFRAME
        },
        isLayerStructure() {
            return this.currentLayer === VIEWER.LAYER.STRUCTURE
        },

        uri() {
            return this.asset ? `${this.getUrl}/api/assets/${this.asset.id}/shared/files/default/generio_model_${this.asset.id}.glb` : ''
        },   

        guidance() {
            if (this.isModeModify && !this.hasSelection) return 'Highlight areas on the model you want to change.'
            return ''
        },
        actions() {
            void this.actionsKey

            // view mode actions
            if (this.isModeView)
            {
                const actions = []
                if (this.isClonable) actions.push(ACTION.TRY_AGAIN) // TODO needs fixing
                if (this.isTextureHealthy || this.isAdministrator) actions.push(ACTION.GENERATE_TEXTURE)
                if (!this.isLegacy && !this.isWatertight) actions.push(ACTION.MAKE_WATERTIGHT)
                if (!this.isLegacy) actions.push(ACTION.REFINE_MODEL)
                
                return actions
            }

            // modify mode actions
            if (this.isModeModify) 
            {
                /*
                if (this.hasSelection)
                    return [ACTION.GENERATE_MODEL]
                */
                return []
            }
		},

        title() {
            return 'Your generated 3D model.'
        },

        tooltipLocation() {
            return 'end'
        },

        index() {
            return this.card.index
        },
        stage() {
            return this.card.stage
        },
        assets() {
            return this.card.assets || []
        },
        asset() {
            return this.assets?.length > 0 ? this.assets[0] : null
        },
        version() {
            return this.asset?.additional?.version ?? 0
        },
        category() {
            return this.card.category
        },
        parent() {
            return this.asset?.parent
        },
        prompt() {
            return this.card.prompt
        },
        promptUser() {
			return this.prompt?.user
		},
        promptQuality() {
            return this.prompt?.quality
        },
        promptSolid() {
            return this.prompt?.solid
        },
        rating() {
            return this.asset?.additional?.rating
        },
        file() {
            return this.currentFile
        },
        fileState() {
            return this.file?.state
        },
        fileDetails() {
            return this.file?.details
        },
	},
	watch: {
        isLocked(newValue, oldValue) {
            if (newValue === oldValue) return
            setTimeout(() => {
                this.$refs.viewer?.handleResize()
            }, 300)
        },
        isFullscreen(newValue, oldValue) {
			if (newValue === oldValue) return
			this.$nextTick(() => {
                this.$refs.viewer?.handleResize()
			})
		},
		isActiveCard(newValue, oldValue) {
			if (newValue === oldValue) return
			if (!newValue) return
			this.$nextTick(() => {
				this.$refs.viewer?.handleResize()
			})
		},
        fileState(newValue, oldValue) {
            if (newValue === oldValue) return
            if (newValue !== this.keyFileStateRetrieved) return
            if (!this.hasRating) this.startTimer()
        },
        'fileDetails.version'(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.actionsKey++
            }
        },
        currentMode(newValue, oldValue) {
            if (newValue === oldValue) return
            if (newValue === VIEWER.MODE.MODIFY) {
                this.animated = false
                this.currentLayer = VIEWER.LAYER.STRUCTURE
            } 
            /*else {
                if (this.transparent) {
                    this.currentLayer = VIEWER.LAYER.TRANSPARENT
                } else if (this.appearance) {
                    this.currentLayer = VIEWER.LAYER.TEXTURE
                } else {
                    this.currentLayer = VIEWER.LAYER.SOLID
                }
            }*/
        },
        stage() {
            this.updateFile()
            this.updateStage()
        }
    },
	methods: {
        ...mapActions('assets', [
            'updateShared'
        ]),
        ...mapActions('dialogs', [
            'open'
        ]),
        ...mapActions('publics', [
            'fetchStatus'
        ]),
        ...mapActions('flows', [
            'newTransaction',
            'commitUpdateAssets',
            'fetchBase',
            'fetchGenerateModel'
        ]),
        async updateStatus() 
        {
            this.healthy = {
                texture: true
			}

            try
            {
                // get status
                const status = await this.fetchStatus()

                // has healthy services
				if (status?.healthy) 
				{
                    const texture = status.healthy['model_texture_generate']
                    if (texture === false) this.healthy.texture = false
				}
            }
            catch (error)
            {
                // silent fail - if status check does not work, we still want to proceed
            }
        },
        handleInit() {
            this.updateFile()
            this.updateStage()
        },
        handleLoading(loading) {
            this.loading = loading
            this.generating = loading
        },
        handleError() {
            this.error = true
            this.message = 'Model generation has failed. Please try again.'
        },
        handlePause() {
            this.animated = false
        },
        handleLoaded(manager, information) {
            if (manager === 'coords') {
                this.loadedCoords = true
            } 
            else if (manager === 'data') {
                // get information about loaded model data
                this.texture = information?.texture?.any ?? information?.texture?.albedo ?? false
                this.appearance = information?.appearance ?? this.texture
                this.loadedDataGeometry = false
                this.loadedDataTexture = false
                this.transparent = information?.texture?.transparent ?? false
                
                // handling loaded data types
                if (this.transparent) {
                    this.currentLayer = VIEWER.LAYER.TRANSPARENT
                    this.loadedDataTexture = true
                }
                else if (this.appearance) {
                    this.currentLayer = VIEWER.LAYER.TEXTURE
                    this.loadedDataTexture = true
                } 
                else {
                    this.currentLayer = VIEWER.LAYER.SOLID
                    this.loadedDataGeometry = true
                }
            }
            this.currentStage = this.stage
        },
        handleLayer(layer) {
            this.currentLayer = layer
        },
        handleSelection(selected) {
            this.selected = selected
        },
        handleAction(action) {
            this.stopTimer()

            // adjust augmentation
            const augmentation = this.card.prompt.augmentation
            // remove poses and views from augmentation
            if (augmentation) {
                delete augmentation.poses
                delete augmentation.views
            }
            this.card.prompt.augmentation = augmentation

            switch (action.id) 
            {
                /*
                case ACTION.GENERATE_MODEL.id:
					this.newTransaction({
                        app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchGenerateModel({
							component: CARD.MODEL,
                            mask: this.$refs.viewer.getMask(),
                            quality: this.promptQuality,
                            solid: this.promptSolid,
                            geometry: true,
                            texture: false
						})
					})
					break
                */

                case ACTION.GENERATE_TEXTURE.id:
					this.newTransaction({
                        app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
                        taskFetch: this.fetchBase({
                            component: CARD.SELECT_TEXTURE,
                            category: CATEGORY.MODEL
                        })
					})
					break

                case ACTION.MAKE_WATERTIGHT.id:
                    this.newTransaction({
                        app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchBase({
							component: CARD.WATERTIGHT,
							category: CATEGORY.MODEL,
						})
					})
                    break

                case ACTION.REFINE_MODEL.id:
                    this.newTransaction({
                        app: this.app,
						card: this.card,
						taskCommit: this.commitUpdateAssets(),
						taskFetch: this.fetchBase({
							component: CARD.OPTIMIZE,
							category: CATEGORY.MODEL,
						})
					})
                    break

                case ACTION.TRY_AGAIN.id:
                    this.newTransaction({
                        app: this.app,
						card: this.card,
                        taskCommit: this.commitUpdateAssets({ 
                            skipped: true
                        }),
                        taskFetch: this.card.transaction.taskFetch.clone()
					})
                    break
            }
        },
        handleToggleAnimation() {
            this.animated = !this.animated
        },
        handleRating() {
            this.stopTimer()
            this.open({
                component: 'rating',
                payload: {
                    asset: this.asset
                }
            })
        },
        handleDownload() {
            this.stopTimer()
            this.open({
                component: 'download',
                payload: {
                    asset: this.asset
                }
            })
        },
        handleShare() {
            this.stopTimer()
            this.open({
                component: 'share',
                payload: {
                    asset: this.asset
                }
            })
        },
        handleShowcase() {
            this.updateShared({
                asset: this.asset,
                shared: 1
            })
            .then(response => {
                window.location.href = `intent://arvr.google.com/scene-viewer/1.0?file=${this.uri}&mode=ar_preferred#Intent;scheme=https;package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=${this.uri};end;`
            })
        },

        updateFile() {
            const file = this.assets?.[0]?.files[ASSETS.FILES.KEY.DEFAULT] || null
            this.currentFile = { 
                coords: file?.coords || null,
                data: file?.data || null,
                state: file?.state || null,
                details: file?.details || null
            }
            this.currentAsset = {
                type: ASSETS.TYPES.MODEL,
                generated: this.asset?.generated ?? false,
                files: {
                    [ASSETS.FILES.KEY.DEFAULT]: this.currentFile
                },
                additional: this.asset?.additional ?? {}
            }
        },
        updateStage() {
            switch (this.stage) {
                case VIEWER.STAGE.NOISE:
                    this.currentStage = VIEWER.STAGE.NOISE
                    break
                case VIEWER.STAGE.ANALYZE:
                    this.currentStage = VIEWER.STAGE.ANALYZE
                    break
                case VIEWER.STAGE.STRUCTURE:
                    if (this.loadedCoords) {
                        this.currentStage = VIEWER.STAGE.STRUCTURE
                    }
                    break
                case VIEWER.STAGE.GEOMETRY:
                    if (this.loadedDataGeometry) {
                        this.currentStage = VIEWER.STAGE.GEOMETRY
                    }
                    break
                case VIEWER.STAGE.MAPPING:
                    if (this.loadedDataTexture) {
                        this.currentStage = VIEWER.STAGE.MAPPING
                    }
                    break
                case VIEWER.STAGE.TEXTURE:
                    if (this.loadedDataTexture) {
                        this.currentStage = VIEWER.STAGE.TEXTURE
                    }
                    break
            }
        },
        startTimer() {
            if (this.timer) {
                clearTimeout(this.timer)
            }
            this.observedAsset = this.asset.id
            this.timer = setTimeout(() => {
                if (this.observedAsset === this.asset.id && this.isCurrentCard) this.handleRating()
            }, 15000) // 15 secs
        },
        stopTimer() {
            if (this.timer) {
                clearTimeout(this.timer)
                this.timer = null
            }
        }
	},
    mounted() {
        this.animated = true
        this.updateStatus()
        this.handleInit()
    },
    beforeUnmount() {
        this.stopTimer()
    }
}
</script>

<style scoped>
.toggle {
    flex-shrink: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: unset;
    border-radius: 8px;
}

.toggle .v-btn:not(:first-child) {
    border-top: 1px solid rgb(var(--v-theme-lightbase));
}

.toggle .v-btn {
    flex-grow: 1;
    min-height: 50px;
    border-radius: 0px !important;
    background: rgb(var(--v-theme-darkbase));
}

.toggle .v-btn.active {
    color: rgb(var(--v-theme-white)) !important;
    background: linear-gradient(0deg, rgb(var(--v-theme-color2)), rgb(var(--v-theme-color1)));
}

.button.active {
    color: rgb(var(--v-theme-white)) !important;
    background: linear-gradient(0deg, rgb(var(--v-theme-color2)), rgb(var(--v-theme-color1)));
}

.card-model-main {
    width: 100%;
    height: 100%;
}

@media (max-width: 800px) {
    .toggle {
        flex-direction: row;
        width: unset;
    }

    .toggle .v-btn {
        min-height: 40px;
    }

    .toggle .v-btn:not(:first-child) {
        border-top: none;
        border-left: 1px solid rgb(var(--v-theme-lightbase));
    }
}
</style>